import React, { useMemo, useState, useEffect } from "react";
import HistorySection from "./HistorySection";
import { buildBaseKey, loadFilters, saveFilters, matchLogicalQuery } from "./historyUtils";
import { matchesGlobalEventFilters } from "../utils/filters";
import AiAssessmentsTab from "../assessments/AiAssessmentsTab";

export default function HistoryHome({
  history, insights, globalFilters, openPopup, collapseCommand, settings, setSettings,
}) {
  const [filters, setFilters] = useState(() => loadFilters());
  const [view, setView] = useState(() => new URLSearchParams(window.location.search).get("historyView") === "ai-assessments"
    ? "assessments" : "events");

  const changeView = (nextView) => {
    const url = new URL(window.location.href);
    url.searchParams.set("view", "history");
    if (nextView === "assessments") url.searchParams.set("historyView", "ai-assessments");
    else {
      url.searchParams.delete("historyView");
      url.searchParams.delete("assessment");
    }
    window.history.replaceState({}, "", url);
    setView(nextView);
  };

  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  const flatEvents = useMemo(() => {
    const out = [];
    const sections = history?.sections || [];
    for (const s of sections) {
      const title = (s?.title || "").trim();
      if (!title) continue;
      const evs = s?.events || [];
      for (const e of evs) {
        if (!e?.cur) continue;
        out.push({
          ...e,
          sectionTitle: title,
          baseKey: buildBaseKey(e),
        });
      }
    }
    // newest first
    out.sort((a, b) => (b.ts || 0) - (a.ts || 0));
    return out;
  }, [history]);

  const sections = useMemo(() => (history?.sections || []).filter(s => s?.title), [history]);

  // Global search: supports plain text or logical expressions, e.g. "(reversal or grab) and DEGO"
  const searchQuery = (filters.q || "").trim();

  const filteredFlat = useMemo(() => {
    const globalMatches = flatEvents.filter((event) => matchesGlobalEventFilters(event, globalFilters));
    if (!searchQuery) return globalMatches;
    return globalMatches.filter((e) => {
      const hay = `${e.cur} ${e.name || ""} ${e.desc || ""} ${e.sectionTitle}`;
      return matchLogicalQuery(searchQuery, hay);
    });
  }, [flatEvents, globalFilters, searchQuery]);

  return (
    <div className="historyBody">
      <div className="historyViewSwitch" role="group" aria-label="History view">
        <button type="button" className={view === "events" ? "active" : ""}
          aria-pressed={view === "events"} onClick={() => changeView("events")}>Events</button>
        <button type="button" className={view === "assessments" ? "active" : ""}
          aria-pressed={view === "assessments"} onClick={() => changeView("assessments")}>
          AI Assessments <span className="ultimateBadge">Ultimate</span>
        </button>
      </div>
      {view === "assessments" ? <AiAssessmentsTab
        insights={insights}
        settings={settings}
        setSettings={setSettings}
      /> : <>
      <div className="historyFilters">
        <div className="historyFiltersRow">
          <div className="historyFiltersLabel">Range</div>
          <select
            className="historySelect"
            value={filters.range}
            onChange={(ev) => setFilters((f) => ({ ...f, range: ev.target.value }))}
          >
            <option value="all">All</option>
            <option value="1h">1h</option>
            <option value="4h">4h</option>
            <option value="24h">24h</option>
            <option value="7d">7d</option>
            <option value="30d">30d</option>
          </select>

          <div className="historyFiltersLabel">2x+</div>
          <input
            type="checkbox"
            checked={filters.occ2}
            onChange={(ev) => setFilters((f) => ({ ...f, occ2: ev.target.checked }))}
          />
        </div>

        <div className="historyFiltersRow">
          <input
            className="historyInput"
            placeholder="Search or logical expr., e.g. (reversal or grab) and DEGO"
            value={filters.q}
            onChange={(ev) => setFilters((f) => ({ ...f, q: ev.target.value }))}
          />
        </div>
      </div>

      <div className="historySections">
        {sections.map((s) => (
          <HistorySection
            key={s.title}
            section={s}
            allEvents={filteredFlat}
            range={filters.range}
            occ2={filters.occ2}
            insights={insights}
            openPopup={openPopup}
            collapseCommand={collapseCommand}
          />
        ))}
      </div>
      </>}
    </div>
  );
}
